import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Timer } from 'lucide-react';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const defaults = {
  id: 'countdown',
  enabled: true,
  target_iso: '2026-09-18T07:30:00+08:00',
  label: 'Tee-off in',
  subtitle: '18 Sept 2026 · Kelab Golf Negara Subang',
  register_url: '',
  cta_label: 'Register →',
  show_sparkles: true,
  show_marquee: true,
  marquee_text: '',
  live_message: '🏌️ Charity Golf is LIVE — Tap to register/sponsor',
};

// Convert ISO with tz to "YYYY-MM-DDTHH:mm" for datetime-local input (local tz Malaysia +08:00)
const isoToLocal = (iso) => {
  try {
    const d = new Date(iso);
    const off = 8 * 60; // Malaysia offset in minutes
    const local = new Date(d.getTime() + (off - d.getTimezoneOffset()) * 60000 * -1);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

// Convert "YYYY-MM-DDTHH:mm" (interpreted as Malaysia time) back to ISO with +08:00 tz
const localToIso = (local) => {
  if (!local) return '';
  return `${local}:00+08:00`;
};

const CountdownManager = () => {
  const [data, setData] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/countdown');
      if (res.data && res.data.id) setData({ ...defaults, ...res.data });
    } catch (e) {
      // ignore
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:countdown',
    data,
    enabled: loaded,
    savedAt: data.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setData({ ...defaults, ...draft });
    discardDraft();
  };

  const save = async () => {
    setLoading(true);
    try {
      await adminApi.post('/countdown', data);
      const res = await adminApi.get('/countdown');
      if (res.data) setData({ ...defaults, ...res.data });
      markSaved(res.data || data);
      toast({ title: 'Saved', description: 'Countdown updated. Live in seconds.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Compute a live preview
  const now = Date.now();
  const diff = Math.max(0, new Date(data.target_iso).getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);

  return (
    <div className="space-y-6" data-testid="countdown-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="countdown-draft-banner"
        />
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Timer className="w-6 h-6 text-[#1565C0]" />
          <div>
            <h2 className="text-2xl font-bold">Homepage Countdown Strip</h2>
            <p className="text-xs text-gray-500">Controls the timer bar directly below the announcement banner.</p>
          </div>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="countdown-enabled" className="text-sm">Enabled</Label>
            <Switch
              id="countdown-enabled"
              checked={data.enabled}
              onCheckedChange={(v) => set('enabled', v)}
              data-testid="countdown-enabled-switch"
          />
        </div>
        <HistoryDialog collection="countdown" docId="countdown" onRestored={load} />
      </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card>
          <CardHeader><CardTitle>Countdown Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Target date & time (Malaysia, UTC+8)</Label>
              <Input
                type="datetime-local"
                value={isoToLocal(data.target_iso)}
                onChange={(e) => set('target_iso', localToIso(e.target.value))}
                data-testid="countdown-target-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Currently set to: <code className="bg-gray-100 px-1">{data.target_iso}</code>
              </p>
            </div>

            <div>
              <Label>Prefix Label</Label>
              <Input
                value={data.label}
                onChange={(e) => set('label', e.target.value)}
                placeholder="Tee-off in"
                data-testid="countdown-label-input"
              />
            </div>

            <div>
              <Label>Subtitle (shown right of the timer)</Label>
              <Input
                value={data.subtitle}
                onChange={(e) => set('subtitle', e.target.value)}
                placeholder="18 Sept 2026 · Kelab Golf Negara Subang"
                data-testid="countdown-subtitle-input"
              />
            </div>

            <div>
              <Label>Register / Click-through URL</Label>
              <Input
                value={data.register_url}
                onChange={(e) => set('register_url', e.target.value)}
                placeholder="https://wa.me/... or https://forms.gle/..."
                data-testid="countdown-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">Opens when visitors tap the countdown strip.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>CTA Button Label</Label>
                <Input
                  value={data.cta_label}
                  onChange={(e) => set('cta_label', e.target.value)}
                  placeholder="Register →"
                />
              </div>
              <div>
                <Label>Live-day Message</Label>
                <Input
                  value={data.live_message}
                  onChange={(e) => set('live_message', e.target.value)}
                  placeholder="🏌️ LIVE — Tap to register"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-sparkles"
                  checked={data.show_sparkles}
                  onCheckedChange={(v) => set('show_sparkles', v)}
                  data-testid="show-sparkles-switch"
                />
                <Label htmlFor="show-sparkles">Sparkles</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-marquee"
                  checked={data.show_marquee}
                  onCheckedChange={(v) => set('show_marquee', v)}
                  data-testid="show-marquee-switch"
                />
                <Label htmlFor="show-marquee">Marquee ribbon</Label>
              </div>
            </div>

            {data.show_marquee && (
              <div>
                <Label>Marquee Text</Label>
                <Textarea
                  value={data.marquee_text}
                  onChange={(e) => set('marquee_text', e.target.value)}
                  rows={3}
                  placeholder="⛳ HIT THE GREEN · LIFT A LIFE · ..."
                  data-testid="countdown-marquee-input"
                />
                <p className="text-xs text-gray-500 mt-1">Use ` · ` between phrases; the ribbon loops seamlessly.</p>
              </div>
            )}

            <Button onClick={save} disabled={loading} className="bg-[#1565C0]" data-testid="save-countdown-btn">
              <Save className="w-4 h-4 mr-2" />{loading ? 'Saving…' : 'Save Countdown'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#D4AF37]/40">
          <CardHeader><CardTitle className="text-base">Live Preview</CardTitle></CardHeader>
          <CardContent>
            {data.enabled ? (
              <div className="bg-gradient-to-r from-[#0A1628] via-[#0f2137] to-[#1565C0] py-3 px-4 rounded-lg shadow-inner">
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-[0.25em] text-[#D4AF37]">
                    {data.label}
                  </span>
                  <div className="flex items-start gap-3 text-white">
                    <div className="text-center">
                      <div className="text-2xl font-black tabular-nums">{String(days).padStart(2,'0')}</div>
                      <div className="text-[9px] uppercase tracking-widest opacity-80">Days</div>
                    </div>
                    <div className="text-xl font-black opacity-40 pt-1">:</div>
                    <div className="text-center">
                      <div className="text-2xl font-black tabular-nums">{String(hours).padStart(2,'0')}</div>
                      <div className="text-[9px] uppercase tracking-widest opacity-80">Hrs</div>
                    </div>
                    <div className="text-xl font-black opacity-40 pt-1">:</div>
                    <div className="text-center">
                      <div className="text-2xl font-black tabular-nums">{String(mins).padStart(2,'0')}</div>
                      <div className="text-[9px] uppercase tracking-widest opacity-80">Min</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase text-white/70">· {data.subtitle}</span>
                  {data.cta_label && (
                    <span className="text-[10px] font-black uppercase bg-[#D4AF37] text-[#0A1628] px-3 py-1 rounded-full">
                      {data.cta_label}
                    </span>
                  )}
                </div>
                {data.show_marquee && data.marquee_text && (
                  <div className="mt-2 bg-[#D4AF37] py-1 px-2 rounded overflow-hidden">
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#0A1628] whitespace-nowrap">
                      {data.marquee_text.substring(0, 80)}…
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-24 flex items-center justify-center bg-gray-100 rounded text-gray-500 text-sm">
                Countdown is disabled — toggle "Enabled" ON to make it live.
              </div>
            )}
            <p className="mt-3 text-xs text-gray-600">
              {data.enabled
                ? `✅ Live · counts down to ${new Date(data.target_iso).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' })} (Malaysia)`
                : '⏸ Countdown is hidden on the homepage.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CountdownManager;
